import { GetAvailableCouponsDocument, type GetAvailableCouponsQuery } from '@/generated/root-types';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import {
  couponDiscountTypeLabel,
  couponTypeLabel,
  formatCouponDiscount,
} from '@/utils/couponLabels';
import { useQuery } from '@apollo/client/react';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@gastrosoft/shared/ui';

type AvailableCoupon = NonNullable<
  NonNullable<GetAvailableCouponsQuery['availableCoupons']>['nodes']
>[number];

interface CouponMetaRowProps {
  label: string;
  value: string;
}

function CouponMetaRow({ label, value }: CouponMetaRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} textAlign="right">
        {value}
      </Typography>
    </Box>
  );
}

interface AvailableCouponCardProps {
  coupon: AvailableCoupon;
  locale: string;
}

function AvailableCouponCard({ coupon, locale }: AvailableCouponCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSuccess } = useNotification();
  const discountLabel = formatCouponDiscount(t, coupon.discountType, coupon.discountValue, locale);
  const redemptionLimit =
    coupon.maxRedemptions == null
      ? t('coupons.unlimitedRedemptions')
      : String(coupon.maxRedemptions);
  const redemptionValue =
    coupon.maxRedemptions == null
      ? String(coupon.redemptionCount)
      : `${coupon.redemptionCount} / ${coupon.maxRedemptions}`;
  const redemptionProgress =
    coupon.maxRedemptions != null && coupon.maxRedemptions > 0
      ? Math.min(100, (coupon.redemptionCount / coupon.maxRedemptions) * 100)
      : null;
  const expiryValue = coupon.expiryDate
    ? new Date(coupon.expiryDate).toLocaleDateString(locale)
    : t('coupons.noExpiry');
  const typeValue =
    coupon.type === 'DURATION' && coupon.duration != null
      ? `${couponTypeLabel(t, coupon.type)} · ${coupon.duration}`
      : couponTypeLabel(t, coupon.type);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',

        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 52%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: 'primary.main',
        }}
      />
      <CardContent
        sx={{
          flex: 1,
          pl: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '&:last-child': { pb: 2 },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.14em', lineHeight: 1.2 }}
          >
            {coupon.name}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1.25,
            px: 1.5,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: 1,
            borderColor: alpha(theme.palette.primary.main, 0.18),
            transition: (muiTheme) =>
              muiTheme.transitions.create(['box-shadow', 'border-color', 'transform'], {
                duration: muiTheme.transitions.duration.shorter,
              }),
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.45),
              boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.14)}`,
              transform: 'translateY(-2px)',
            },
            cursor: 'pointer',
          }}
          onClick={() => {
            navigator.clipboard.writeText(coupon.code);
            showSuccess(t('coupons.codeCopied'));
          }}
        >
          <Typography
            component="p"
            sx={{
              color: 'primary.main',
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
            variant="h4"
          >
            {discountLabel}
          </Typography>{' '}
          <Typography variant="h6">{coupon.code}</Typography> <ContentCopy />
        </Box>

        {coupon.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {coupon.description}
          </Typography>
        ) : null}

        <Box
          sx={{
            borderTop: 1,
            borderStyle: 'dashed',
            borderColor: 'divider',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: 'auto',
          }}
        >
          <CouponMetaRow label={t('common.type')} value={typeValue} />
          <CouponMetaRow label={t('coupons.maxRedemptions')} value={redemptionLimit} />
          <Box>
            <CouponMetaRow label={t('coupons.redemptionCount')} value={redemptionValue} />
            {redemptionProgress != null ? (
              <LinearProgress
                variant="determinate"
                value={redemptionProgress}
                sx={{
                  mt: 0.75,
                  height: 5,
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  '& .MuiLinearProgress-bar': { borderRadius: 999 },
                }}
              />
            ) : null}
          </Box>
          <CouponMetaRow label={t('coupons.expiryDate')} value={expiryValue} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AvailableCoupons() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const { data, loading } = useQuery(GetAvailableCouponsDocument, {
    variables: {
      first: GRAPHQL_MAX_PAGE_SIZE,
      where: { status: { eq: 'ACTIVE' } },
      order: [{ discountValue: 'DESC' }],
    },
  });

  const coupons = data?.availableCoupons?.nodes ?? [];

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 1, sm: 2 },
        py: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
            color: 'primary.main',
          }}
        >
          <LocalOfferIcon fontSize="small" aria-hidden />
        </Box>
        <Box>
          <Typography variant="h4">{t('nav.availableCoupons')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? t('globalSearch.searching')
              : t('coupons.availableCount', { count: coupons.length })}
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : coupons.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 2,
            textAlign: 'center',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <LocalOfferIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} aria-hidden />
          <Typography color="text.secondary">{t('coupons.availableEmpty')}</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {coupons.map((coupon) => (
            <Grid key={coupon.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <AvailableCouponCard coupon={coupon} locale={locale} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
